/**
 * The marks that make the cold-load target measurable, and the boundary each one
 * actually names.
 *
 * ┌─ ⛔ WHY THIS EXISTS: THREE MEASUREMENTS FAILED TO MEASURE THE TARGET ───────┐
 * │ The target (INBOX scope item 24) is **"navigation start to the inbox list   │
 * │ rendered and scrollable, under 1 second"**.                                 │
 * │                                                                            │
 * │ 2026-08-05, dev server: 1,900 ms to first paint, 39 requests, 3.9 MB. The   │
 * │ render term was not visible underneath the dev server's own cost.            │
 * │                                                                            │
 * │ 2026-08-07, preview build: first-contentful-paint at **648 ms**, and the    │
 * │ API request did not return until **782 ms**. FCP marked the SHELL. Wrong by │
 * │ an unknown amount in the optimistic direction, and it looked like a result. │
 * │                                                                            │
 * │ 2026-08-07, first version of this file: one mark, taken during React's      │
 * │ render. **Also optimistic** - see LIST_PAINTED_MARK below.                  │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ **THREE BOUNDARIES, NAMED SEPARATELY, BECAUSE THEY ARE DIFFERENT EVENTS AND
 * THE GAPS BETWEEN THEM ARE THE POINT.** A single "the list loaded" number hides
 * which of these it means, and every version of this measurement so far has been
 * wrong about exactly that.
 *
 *   inbox-data-arrived    the JSON was parsed. MEASURED, not derived.
 *   inbox-list-rendered   React produced the rows. Layout and paint have NOT
 *                         happened yet.
 *   inbox-list-painted    the browser painted the frame containing them, and
 *                         the container was checked for overflow.
 *
 * ⚠️ **`inbox-list-painted` IS THE ONLY ONE THAT MEANS WHAT A PERSON MEANS.**
 * The other two are diagnostics for attributing where the time went.
 */
export const DATA_ARRIVED_MARK = 'inbox-data-arrived'
export const LIST_RENDERED_MARK = 'inbox-list-rendered'
export const LIST_PAINTED_MARK = 'inbox-list-painted'

/**
 * Whether the list container actually overflowed when it was painted.
 *
 * ⛔ **"SCROLLABLE" IS IN THE REQUIREMENT AND WAS NEVER ASSERTED.** Fifty rows
 * present in the DOM does not establish it: the container may still be laying
 * out, may have zero height from a missing style, or may not overflow at all.
 * `scrollHeight > clientHeight` at paint time is what establishes it, and it is
 * recorded rather than assumed so a measurement taken against a list that never
 * became scrollable is distinguishable from one that did.
 */
let scrollable: boolean | null = null

/**
 * ⛔ AND THIS FIELD IS ITSELF A RECORDED FINDING:
 * `check-invisible-to-its-own-runner` in `ops/deferrals.txt`, owner INBOX.
 *
 * jsdom computes no layout, so `scrollHeight` and `clientHeight` are both zero
 * and this can only ever be `false` there - **by construction, not by defect.**
 * A CHECK WHOSE SUBJECT IS STRUCTURALLY INVISIBLE TO THE ENVIRONMENT THAT RUNS
 * IT is a third variant, distinct from a vacuous assertion and from a scan too
 * narrow, and it would read as a real failure to anyone who found it.
 *
 * ⚠️ It is safe today only because this RECORDS what it observed instead of
 * asserting it. The implied rule is unwritten: a property's owner must be an
 * environment that can SEE it, and "assert it everywhere and let the strong
 * environment catch it" produces exactly this.
 */

let dataMarked = false
let renderMarked = false
let paintMarked = false

/** `performance` is absent in non-DOM environments; instrumentation must never throw. */
function mark(name: string): void {
  globalThis.performance?.mark?.(name)
}

/**
 * The moment the conversation JSON finished parsing.
 *
 * ⛔ MEASURED BECAUSE THE ALTERNATIVE WAS SUBTRACTION, AND SUBTRACTION WAS
 * WRONG. The first report derived the render cost as
 * `listRendered - (apiStart + apiDuration)` and got 3-8 ms. **That assumes zero
 * gap between the response arriving on the network and React being scheduled**,
 * and there is a microtask hop plus React's own scheduler in there. The derived
 * figure was under-attributed by however long that is. This measures it.
 */
export function markDataArrived(): void {
  if (dataMarked) return
  dataMarked = true
  mark(DATA_ARRIVED_MARK)
}

/**
 * Called from the list's render path the first time it has rows to show.
 *
 * ⚠️ THIS IS **NOT** THE HONEST BOUNDARY, and it is kept anyway. It fires while
 * React is producing output; layout, style and paint all follow it. Keeping it
 * alongside `inbox-list-painted` is what puts the difference between "React
 * rendered" and "a person can see it" on the record instead of leaving it as an
 * unstated assumption.
 *
 * ┌─ ⚠️ IT TOOK A `rowCount` AND REFUSED ON ZERO. THAT WAS DEAD CODE. ─────────┐
 * │ Removed 2026-08-07 after `ops/mutate.sh` reported NOT CAUGHT: deleting the  │
 * │ `rowCount === 0` clause failed no test. The three "does not fire on loading │
 * │ / empty / error" tests pass because **`ConversationList` RETURNS EARLY** in │
 * │ all three cases and never reaches the call. The guard was a second copy of  │
 * │ an invariant the control flow already enforced, and a duplicated invariant  │
 * │ drifts while both copies look correct.                                      │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export function markListRendered(): void {
  if (renderMarked) return
  renderMarked = true
  mark(LIST_RENDERED_MARK)
}

/**
 * The moment the browser painted the frame containing the rows.
 *
 * ┌─ ⛔ WHY DOUBLE `requestAnimationFrame` AND NOT ONE, OR AN EFFECT ALONE ─────┐
 * │ An effect runs after React commits, still BEFORE the browser has laid out   │
 * │ and painted. One rAF callback runs BEFORE the paint of the frame it is      │
 * │ scheduled in. **The callback of a rAF scheduled INSIDE a rAF callback is    │
 * │ the first code that runs after that paint has happened**, which is the      │
 * │ closest a page can get to "the pixels are on the screen".                   │
 * │                                                                            │
 * │ ⚠️ IT IS AN UPPER BOUND, NOT AN EXACT INSTANT. It fires at the start of the │
 * │ next frame, so it over-reports by up to one frame interval - about 16 ms at │
 * │ 60 Hz. **That is the right direction to be wrong**: the previous three      │
 * │ attempts were all optimistic by unknown amounts.                            │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export function markListPainted(el: { scrollHeight: number; clientHeight: number } | null): void {
  if (paintMarked) return
  paintMarked = true
  const raf = globalThis.requestAnimationFrame
  if (!raf) {
    // No rAF (jsdom, SSR). Mark synchronously rather than never: a missing mark
    // reads as "the list never rendered", which is a different and worse claim.
    scrollable = el ? el.scrollHeight > el.clientHeight : null
    mark(LIST_PAINTED_MARK)
    return
  }
  raf(() => {
    raf(() => {
      scrollable = el ? el.scrollHeight > el.clientHeight : null
      mark(LIST_PAINTED_MARK)
      recordSampleIfEnabled()
    })
  })
}

/** Turn the recorder on: `localStorage.setItem(COLDLOAD_FLAG, 'on')`. */
export const COLDLOAD_FLAG = 'coldload'
/** Where samples accumulate, one per page load. */
export const COLDLOAD_SAMPLES = 'coldload-samples'

/**
 * Append this page load's timings to `localStorage`, if the flag is set.
 *
 * ┌─ ⛔ WHY THE PAGE RECORDS ITSELF RATHER THAN A HUMAN PASTING A SNIPPET ──────┐
 * │ The open question is whether the bimodal startup swing is V8's bytecode     │
 * │ cache warming up or DevTools fetching the 1.1 MB source map - **and both    │
 * │ are ORDER-DEPENDENT, so both are contaminated by having DevTools open.**    │
 * │                                                                            │
 * │ Isolating them means loading with DevTools CLOSED, which made the manual    │
 * │ protocol "close DevTools, reload, open DevTools, paste, close DevTools,     │
 * │ reload…" ten times over. ⚠️ **A measurement procedure that tedious gets     │
 * │ done wrong, and a sample taken with DevTools accidentally open is           │
 * │ indistinguishable from one taken correctly.** The protocol was the risk.    │
 * │                                                                            │
 * │ Now: set the flag once, reload with DevTools shut, read the array once.     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **OFF UNLESS EXPLICITLY ENABLED, AND IT WRITES NOTHING WHEN OFF.** The flag
 * is opt-in per browser profile, so this is inert for every real user; it exists
 * because the alternative is a human executing a twenty-step protocol without
 * slipping once.
 */
function recordSampleIfEnabled(): void {
  try {
    const store = globalThis.localStorage
    if (store?.getItem(COLDLOAD_FLAG) !== 'on') return

    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const api = performance
      .getEntriesByType('resource')
      .find((e) => e.name.includes('/api/inbox/conversations')) as
      | PerformanceResourceTiming
      | undefined
    const t = listTimings()
    const round = (v: number | null | undefined): number | null =>
      typeof v === 'number' ? Math.round(v) : null

    const samples = JSON.parse(store.getItem(COLDLOAD_SAMPLES) ?? '[]') as unknown[]
    samples.push({
      interactive: round(nav?.domInteractive),
      apiStart: round(api?.startTime),
      apiEnd: round(api?.responseEnd),
      dataArrived: round(t.dataArrived),
      rendered: round(t.rendered),
      painted: round(t.painted),
      scrollable: t.scrollable,
      rows: document.querySelectorAll('[data-testid="conversation-item"]').length,
    })
    store.setItem(COLDLOAD_SAMPLES, JSON.stringify(samples))
  } catch {
    // ⚠️ SILENT ON PURPOSE. This is instrumentation; a quota error or a
    // storage-disabled profile must never be the reason the inbox fails to
    // render. A missing sample is visible in the count; a broken list is not
    // worth a measurement.
  }
}

/** Test seam: forget every mark was taken. */
export function resetListMarks(): void {
  dataMarked = false
  renderMarked = false
  paintMarked = false
  scrollable = null
}

function at(name: string): number | null {
  const entry = globalThis.performance?.getEntriesByName?.(name)?.[0]
  return entry ? entry.startTime : null
}

/**
 * Every boundary at once, for the console snippet and for anything recording a
 * measurement.
 *
 * ⚠️ `scrollable: false` IS A FAILED MEASUREMENT, NOT A SLOW ONE. The target
 * says "rendered AND scrollable"; a run where the container never overflowed did
 * not measure the target regardless of how good the numbers look.
 */
export function listTimings(): {
  dataArrived: number | null
  rendered: number | null
  painted: number | null
  scrollable: boolean | null
} {
  return {
    dataArrived: at(DATA_ARRIVED_MARK),
    rendered: at(LIST_RENDERED_MARK),
    painted: at(LIST_PAINTED_MARK),
    scrollable,
  }
}
